/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useMemo } from 'react';
import styles from '../../../styles/components/pageComponents/program/EnrollmentModal.module.css';

export interface User {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

export interface EnrollmentModalProps {
  isOpen: boolean;
  title?: string;
  users: User[];
  onClose: () => void;
  onEnroll: (selectedUserIds: string[]) => void;
  loading : boolean;
 
}

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  isOpen,
  title = 'IMPACT Level 1 ICF ACC',
  users,
  onClose,
  onEnroll,
  loading = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset selection when modal opens/closes or user list changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [isOpen, users]);

  const isAllSelected = useMemo(() => {
    return users.length > 0 && selectedIds.size === users.length;
  }, [users, selectedIds]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map((user) => user.id)));
    }
  };

  const handleToggleUser = (userId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedIds(newSelected);
  };

  const handleEnrollClick = () => {
    if (selectedIds.size > 0) {
      onEnroll(Array.from(selectedIds));
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div 
        className={styles.modalContainer} 
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Select All Section */}
        <div className={styles.selectAllWrapper}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={users.length === 0}
            />
            <span>Select All Learners</span>
          </label>
        </div>

        {/* List Container */}
        <div className={styles.listWrapper}>
          <div className={styles.listHeader}>
            <span className={styles.columnName}>Full Name</span>
          </div>

          <div className={styles.listBody}>
            {users.length === 0 ? (
              <div className={styles.emptyState}>No learners available.</div>
            ) : (
              users.map((user) => (
                <label key={user.id} className={styles.listItem}>
                  <div className={styles.userInfo}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selectedIds.has(user.id)}
                      onChange={() => handleToggleUser(user.id)}
                    />
                    
                    <div className={styles.avatar}>
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={`${user.name}'s avatar`} />
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      )}
                    </div>
                    
                    <span className={styles.userName}>{user.name}</span>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.enrollBtn}
            onClick={handleEnrollClick}
            disabled={selectedIds.size === 0}
          >
          {loading ? 'Enrolling...' : 'Enroll Learners'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentModal;